import { useEffect, useRef, useState } from "react";

const MOBILE_VIEWPORT_QUERY = "(max-width: 768px)";
const COMPACT_SCROLL_THRESHOLD = 32;
const EXPANDED_SCROLL_THRESHOLD = 8;
const COMPACT_HEADER_HEIGHT = 56;
const SCROLL_IDLE_DELAY = 120;

type HeaderCollapseState = {
  isCompact: boolean;
  progress: number;
};

type TaskHeaderCollapseState = HeaderCollapseState & {
  taskId: string | null;
};

export const useTaskViewerHeaderCollapse = (
  taskId: string | null,
  sheetClassName: string
): HeaderCollapseState => {
  const [collapseState, setCollapseState] = useState<TaskHeaderCollapseState>({
    taskId,
    isCompact: false,
    progress: 0,
  });
  const scrollIdleTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const mobileViewport = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const reset = () => {
      setCollapseState({ taskId, isCompact: false, progress: 0 });
    };

    const clearScrollIdleTimeout = () => {
      if (!scrollIdleTimeout.current) return;
      clearTimeout(scrollIdleTimeout.current);
      scrollIdleTimeout.current = null;
    };

    const handleScroll = (event: Event) => {
      const scrollContainer = event.target;
      if (
        !(scrollContainer instanceof HTMLElement) ||
        !scrollContainer.classList.contains("sheets__content-wrapper") ||
        !scrollContainer.closest(`.${sheetClassName}`)
      ) {
        return;
      }

      if (!mobileViewport.matches) {
        reset();
        return;
      }

      const header = scrollContainer.previousElementSibling;
      const expandedHeaderHeight =
        header instanceof HTMLElement ? header.scrollHeight : 0;
      const currentHeaderHeight =
        header instanceof HTMLElement ? header.offsetHeight : 0;
      const collapsibleHeaderHeight = Math.max(
        expandedHeaderHeight - COMPACT_HEADER_HEIGHT,
        0
      );
      const currentAvailableScroll =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const expandedAvailableScroll =
        currentAvailableScroll +
        Math.max(expandedHeaderHeight - currentHeaderHeight, 0);
      if (
        expandedAvailableScroll <
        collapsibleHeaderHeight + COMPACT_SCROLL_THRESHOLD
      ) {
        reset();
        return;
      }

      const nextProgress = Math.min(
        Math.max(scrollContainer.scrollTop / COMPACT_SCROLL_THRESHOLD, 0),
        1
      );
      setCollapseState((currentState) => {
        const currentIsCompact =
          currentState.taskId === taskId ? currentState.isCompact : false;
        const isCompact =
          scrollContainer.scrollTop >= COMPACT_SCROLL_THRESHOLD
            ? true
            : scrollContainer.scrollTop < EXPANDED_SCROLL_THRESHOLD
              ? false
              : currentIsCompact;

        return { taskId, isCompact, progress: nextProgress };
      });

      clearScrollIdleTimeout();
      if (nextProgress > 0 && nextProgress < 1) {
        scrollIdleTimeout.current = setTimeout(() => {
          const shouldCompact =
            scrollContainer.scrollTop >= EXPANDED_SCROLL_THRESHOLD;
          scrollContainer.scrollTo({
            top: shouldCompact ? COMPACT_SCROLL_THRESHOLD + 1 : 0,
            behavior: reducedMotion.matches ? "auto" : "smooth",
          });
        }, SCROLL_IDLE_DELAY);
      }
    };

    const handleViewportChange = () => {
      if (!mobileViewport.matches) {
        clearScrollIdleTimeout();
        reset();
      }
    };

    document.addEventListener("scroll", handleScroll, true);
    if (mobileViewport.addEventListener) {
      mobileViewport.addEventListener("change", handleViewportChange);
    } else {
      mobileViewport.addListener(handleViewportChange);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
      clearScrollIdleTimeout();
      if (mobileViewport.removeEventListener) {
        mobileViewport.removeEventListener("change", handleViewportChange);
      } else {
        mobileViewport.removeListener(handleViewportChange);
      }
    };
  }, [sheetClassName, taskId]);

  if (collapseState.taskId !== taskId) {
    return { isCompact: false, progress: 0 };
  }

  return {
    isCompact: collapseState.isCompact,
    progress: collapseState.progress,
  };
};
